import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { devAdminAPI } from '../../services/adminService';
import { clinicAPI, clinicAppointmentsAPI, outpatientAPI, inpatientAPI } from '../../services/api';
import { logAction } from '../../services/auditLogService';
import { useAuth } from '../../hooks/useAuth';
import ConfirmModal from '../../components/channels/modals/ConfirmModal';
import './admin-design.css';
import './DevAdminPage.css';

const DevAdminPage = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { logout } = useAuth();

    const [activeTab, setActiveTab] = useState('users'); // 'users' | 'clinics'

    const [users, setUsers] = useState([]);
    const [selectedUser, setSelectedUser] = useState(null);
    const [isLoadingUsers, setIsLoadingUsers] = useState(false);
    const [isLoadingDetails, setIsLoadingDetails] = useState(false);

    const [clinics, setClinics] = useState([]);
    const [selectedClinic, setSelectedClinic] = useState(null);
    const [clinicAppointments, setClinicAppointments] = useState([]);
    const [clinicOutpatientVisits, setClinicOutpatientVisits] = useState([]);
    const [clinicInpatientVisits, setClinicInpatientVisits] = useState([]);
    const [isLoadingClinics, setIsLoadingClinics] = useState(false);
    const [isLoadingClinicDetails, setIsLoadingClinicDetails] = useState(false);
    const [clinicLoadError, setClinicLoadError] = useState(null);
    const [clinicDetailsError, setClinicDetailsError] = useState(null);

    const [userSearchQuery, setUserSearchQuery] = useState('');
    const [clinicSearchQuery, setClinicSearchQuery] = useState('');

    // Helper to get display name safely
    const getUserName = (user) => {
        if (!user) return 'Unknown User';
        if (user.first_name && user.last_name) return `${user.first_name} ${user.last_name}`;
        if (user.first_name) return user.first_name;
        if (user.name && typeof user.name === 'string') return user.name;
        if (user.display_name && typeof user.display_name === 'string') return user.display_name;
        // Handle case where display_name might be an object (the error source)
        if (user.display_name && typeof user.display_name === 'object' && user.display_name.name) return user.display_name.name;

        if (user.username && typeof user.username === 'string') return user.username;
        if (user.email) return user.email.split('@')[0];
        return 'Unknown Name';
    };

    // Helper to get user type safely
    const getUserType = (user) => {
        if (!user) return '';
        const type = user.user_type || user.userType || user.type || user.role;
        if (typeof type === 'object' && type !== null) {
            return type.name || type.slug || 'User';
        }
        return type || 'User';
    };

    // Memoized filtered users for instant UI feedback
    const filteredUsers = React.useMemo(() => {
        if (!userSearchQuery) return users;
        const lowerQuery = userSearchQuery.toLowerCase();
        return users.filter(user => {
            const name = getUserName(user).toLowerCase();
            const email = (user.email || '').toLowerCase();
            return name.includes(lowerQuery) || email.includes(lowerQuery);
        });
    }, [users, userSearchQuery]);

    const getClinicName = (clinic) => {
        if (!clinic) return 'Unknown Clinic';
        return clinic.name_en || clinic.name_ar || clinic.name || clinic.clinic_name || clinic.clinicName || clinic.display_name || `Clinic #${clinic.id || ''}`;
    };

    const filteredClinics = React.useMemo(() => {
        if (!clinicSearchQuery) return clinics;
        const lowerQuery = clinicSearchQuery.toLowerCase();
        return clinics.filter(c => {
            const name = getClinicName(c).toLowerCase();
            const email = (c.email || '').toLowerCase();
            const phone = (c.phone || c.telephone || c.mobile || '').toLowerCase();
            return name.includes(lowerQuery) || email.includes(lowerQuery) || phone.includes(lowerQuery);
        });
    }, [clinics, clinicSearchQuery]);

    // Modal State
    const [deleteModal, setDeleteModal] = useState({
        isOpen: false,
        type: null, // 'user' or 'pet'
        id: null,
        name: '',
        meta: null
    });

    // Helper to safely render potential objects
    const safeRender = (value) => {
        if (value === null || value === undefined) return '';
        if (typeof value === 'string' || typeof value === 'number') return value;
        if (typeof value === 'object') {
            return value.name || value.display_name || value.label || value.description || '';
        }
        return String(value);
    };

    // Normalize various API shapes into a plain array
    const toArray = (value) => {
        if (!value) return [];
        if (Array.isArray(value)) return value;
        if (Array.isArray(value?.data)) return value.data;
        if (Array.isArray(value?.items)) return value.items;
        return [];
    };

    const extractList = (payload, preferredKeys = []) => {
        if (!payload) return [];
        if (Array.isArray(payload)) return payload;

        // Common wrappers
        const unwrapped = payload?.data ?? payload;

        if (Array.isArray(unwrapped)) return unwrapped;

        for (const key of preferredKeys) {
            const candidate = unwrapped?.[key];
            const arr = toArray(candidate);
            if (arr.length) return arr;
        }

        // Final fallback: try data/data
        return toArray(unwrapped?.data);
    };
    // Load Users
    const loadUsers = async () => {
        setIsLoadingUsers(true);
        try {
            const params = userSearchQuery ? { search: userSearchQuery } : {};
            const response = await devAdminAPI.getUsers(params);

            // The backend confirmed a list of all users at GET /api/v1/dev/users
            const data = response.data;

            // Adapt to response structure
            let userList = [];

            const nestedData = data?.data || data;

            if (Array.isArray(nestedData)) {
                userList = nestedData;
            } else if (nestedData?.users && Array.isArray(nestedData.users)) {
                userList = nestedData.users;
            } else if (nestedData?.data && Array.isArray(nestedData.data)) {
                userList = nestedData.data;
            } else if (Array.isArray(data?.admins) || Array.isArray(data?.dev_admins)) {
                userList = [
                    ...(Array.isArray(data.admins) ? data.admins : []),
                    ...(Array.isArray(data.dev_admins) ? data.dev_admins : [])
                ];
            } else if (data?.dashboard_data?.users && Array.isArray(data.dashboard_data.users)) {
                userList = data.dashboard_data.users;
            }

            setUsers(userList || []);
        } catch (error) {
            console.error("Failed to load users:", error);
        } finally {
            setIsLoadingUsers(false);
        }
    };

    const loadClinics = async () => {
        setIsLoadingClinics(true);
        setClinicLoadError(null);
        try {
            const response = await clinicAPI.getClinics({
                search: clinicSearchQuery || '',
                per_page: 200,
                page: 1
            });
            const raw = response?.data;
            const data = raw?.status === 'success' ? raw.data : raw;

            const nested = data?.data || data;
            let list = [];
            if (Array.isArray(nested)) list = nested;
            else if (Array.isArray(nested?.clinics)) list = nested.clinics;
            else if (Array.isArray(nested?.data)) list = nested.data;
            else if (Array.isArray(data?.clinics)) list = data.clinics;

            setClinics(list || []);
        } catch (e) {
            console.error('Failed to load clinics:', e);
            setClinicLoadError(e?.response?.data?.message || e.message || 'Failed to load clinics');
            setClinics([]);
        } finally {
            setIsLoadingClinics(false);
        }
    };
    // Initial Load
    useEffect(() => {
        loadUsers();
    }, []);

    // Debounced search effect
    useEffect(() => {
        if (!userSearchQuery) return;
        const timer = setTimeout(() => {
            loadUsers(); // Backend search
        }, 500); // 500ms debounce

        return () => clearTimeout(timer);
    }, [userSearchQuery]);

    useEffect(() => {
        if (activeTab !== 'clinics') return;
        loadClinics();
    }, [activeTab]);

    useEffect(() => {
        if (activeTab !== 'clinics') return;
        if (!clinicSearchQuery) return;
        const timer = setTimeout(() => {
            loadClinics();
        }, 500);
        return () => clearTimeout(timer);
    }, [activeTab, clinicSearchQuery]);

    // Handle User Selection
    const handleSelectUser = async (user) => {
        setSelectedUser(user); // Set basic info immediately
        setSelectedClinic(null);
        setIsLoadingDetails(true);
        try {
            const response = await devAdminAPI.getUserDetails(user.id);
            const rawData = response.data;
            let details = rawData?.user || rawData?.data || rawData;

            // Handle case where details might be a single-element array
            if (Array.isArray(details) && details.length > 0) {
                details = details[0];
            }

            // Merge with existing info to avoid losing 'id' or other list-provided data
            setSelectedUser(prev => ({
                ...prev,
                ...details,
                // Ensure critical fields persist or map correctly
                id: details?.id || details?.user_id || details?.pk || prev?.id,
                created_at: details?.created_at || details?.registration_date || prev?.created_at
            }));
        } catch (error) {
            console.error("Failed to load user details:", error);
        } finally {
            setIsLoadingDetails(false);
        }
    };

    const handleSelectClinic = async (clinic) => {
        setSelectedClinic(clinic);
        setSelectedUser(null);
        setClinicDetailsError(null);
        setIsLoadingClinicDetails(true);
        try {
            const clinicId = clinic?.id;
            const [clinicResp, apptResp, outResp, inResp] = await Promise.all([
                clinicId ? clinicAPI.getClinicById(clinicId) : Promise.resolve(null),
                clinicId ? clinicAppointmentsAPI.getClinicAppointments(clinicId, { per_page: 50, sort_by: 'created_at', sort_order: 'desc' }) : Promise.resolve(null),
                clinicId ? clinicAppointmentsAPI.searchOutpatientVisits({ clinic_id: clinicId, per_page: 50, sort_by: 'created_at', sort_order: 'desc' }) : Promise.resolve(null),
                clinicId ? clinicAppointmentsAPI.searchInpatientVisits({ clinic_id: clinicId, per_page: 50, sort_by: 'created_at', sort_order: 'desc' }) : Promise.resolve(null),
            ]);

            const clinicRaw = clinicResp?.data;
            const clinicData = clinicRaw?.status === 'success' ? clinicRaw.data : clinicRaw;
            if (clinicData) setSelectedClinic(prev => ({ ...prev, ...clinicData }));

            const apptRaw = apptResp?.data;
            const apptData = apptRaw?.status === 'success' ? apptRaw.data : apptRaw;
            setClinicAppointments(extractList(apptData, ['appointments']));

            const outRaw = outResp?.data;
            const outData = outRaw?.status === 'success' ? outRaw.data : outRaw;
            setClinicOutpatientVisits(extractList(outData, ['outpatient_visits', 'outpatientVisits', 'visits']));

            const inRaw = inResp?.data;
            const inData = inRaw?.status === 'success' ? inRaw.data : inRaw;
            setClinicInpatientVisits(extractList(inData, ['inpatient_visits', 'inpatientVisits', 'visits']));
        } catch (e) {
            console.error('Failed to load clinic details:', e);
            setClinicDetailsError(e?.response?.data?.message || e.message || 'Failed to load clinic details');
            setClinicAppointments([]);
            setClinicOutpatientVisits([]);
            setClinicInpatientVisits([]);
        } finally {
            setIsLoadingClinicDetails(false);
        }
    };

    // Handle Delete User (Initiate)
    const handleDeleteUserCheck = (user) => {
        setDeleteModal({
            isOpen: true,
            type: 'user',
            id: user.id,
            name: getUserName(user)
        });
    };

    // Handle Delete Pet (Initiate)
    const handleDeletePetCheck = (pet) => {
        setDeleteModal({
            isOpen: true,
            type: 'pet',
            id: pet.id,
            name: pet.name || 'Unknown Pet',
            meta: null
        });
    };

    const handleDeleteClinicItemCheck = (type, item) => {
        const id = item?.id;
        const label =
            type === 'appointment' ? `Appointment #${id}` :
                type === 'outpatient_visit' ? `Outpatient Visit #${id}` :
                    type === 'inpatient_visit' ? `Inpatient Visit #${id}` :
                        `Item #${id}`;
        setDeleteModal({
            isOpen: true,
            type,
            id,
            name: label,
            meta: { clinicId: selectedClinic?.id }
        });
    };

    const handleDeleteClinicBulkCheck = (type, items) => {
        const ids = (Array.isArray(items) ? items : [])
            .map(x => x?.id)
            .filter(Boolean);
        const label =
            type === 'appointments_bulk' ? 'all appointments' :
                type === 'outpatient_bulk' ? 'all outpatient visits' :
                    type === 'inpatient_bulk' ? 'all inpatient visits' :
                        'all items';
        setDeleteModal({
            isOpen: true,
            type,
            id: null,
            name: `${label} (${ids.length})`,
            meta: { clinicId: selectedClinic?.id, ids }
        });
    };

    const deleteMany = async (ids, fn, concurrency = 4) => {
        const queue = [...ids];
        const workers = Array.from({ length: Math.min(concurrency, queue.length) }).map(async () => {
            while (queue.length) {
                const nextId = queue.shift();
                if (!nextId) continue;
                await fn(nextId);
            }
        });
        await Promise.all(workers);
    };

    // Audit-log metadata per deletion type. 'user' and 'pet' are intentionally
    // absent: devAdminAPI.deleteUser and devAdminAPI.deletePet are already
    // wrapped with withAuditLog (pet name is passed via the options argument).
    const DELETE_AUDIT_MAP = {
        appointment: { actionType: 'CLINIC_APPOINTMENT_DELETE', targetType: 'appointment' },
        outpatient_visit: { actionType: 'CLINIC_OUTPATIENT_DELETE', targetType: 'outpatient_visit' },
        inpatient_visit: { actionType: 'CLINIC_INPATIENT_DELETE', targetType: 'inpatient_visit' },
        appointments_bulk: { actionType: 'CLINIC_APPOINTMENT_DELETE', targetType: 'appointment' },
        outpatient_bulk: { actionType: 'CLINIC_OUTPATIENT_DELETE', targetType: 'outpatient_visit' },
        inpatient_bulk: { actionType: 'CLINIC_INPATIENT_DELETE', targetType: 'inpatient_visit' },
    };

    const logDeleteAction = (type, { id, name, bulkIds, status = 'success', errorMessage = null } = {}) => {
        const audit = DELETE_AUDIT_MAP[type];
        if (!audit) return;
        const isBulk = Array.isArray(bulkIds);
        logAction({
            actionType: audit.actionType,
            targetType: audit.targetType,
            targetId: isBulk ? null : (id ?? null),
            targetName: isBulk ? null : (name || null),
            details: isBulk ? { count: bulkIds.length, ids: bulkIds } : {},
            status,
            errorMessage,
        }).catch(() => {});
    };

    // Confirm Delete Action
    const handleConfirmDelete = async () => {
        const { type, id, name, meta } = deleteModal;
        const bulkIds = meta?.ids;
        if (!type || (!id && !Array.isArray(bulkIds))) {
            console.error(`ID or Type missing for ${type} deletion:`, { type, id });
            alert(`Could not delete: ${type} ID is missing.`);
            setDeleteModal({ isOpen: false, type: null, id: null, name: '', meta: null });
            return;
        }

        try {
            if (type === 'user') {
                await devAdminAPI.deleteUser(id);
                setSelectedUser(null);
                loadUsers();
            } else if (type === 'pet') {
                await devAdminAPI.deletePet(id, { targetName: name });
                // Refresh detailed view
                if (selectedUser) {
                    handleSelectUser(selectedUser);
                }
            } else if (type === 'appointment') {
                await clinicAppointmentsAPI.deleteAppointment(id);
                if (selectedClinic) await handleSelectClinic(selectedClinic);
            } else if (type === 'outpatient_visit') {
                await outpatientAPI.deleteOutpatientVisit(id);
                if (selectedClinic) await handleSelectClinic(selectedClinic);
            } else if (type === 'inpatient_visit') {
                await inpatientAPI.deleteInpatientVisit(id);
                if (selectedClinic) await handleSelectClinic(selectedClinic);
            } else if (type === 'appointments_bulk') {
                await deleteMany(bulkIds, (x) => clinicAppointmentsAPI.deleteAppointment(x));
                if (selectedClinic) await handleSelectClinic(selectedClinic);
            } else if (type === 'outpatient_bulk') {
                await deleteMany(bulkIds, (x) => outpatientAPI.deleteOutpatientVisit(x));
                if (selectedClinic) await handleSelectClinic(selectedClinic);
            } else if (type === 'inpatient_bulk') {
                await deleteMany(bulkIds, (x) => inpatientAPI.deleteInpatientVisit(x));
                if (selectedClinic) await handleSelectClinic(selectedClinic);
            }
            logDeleteAction(type, { id, name, bulkIds });
        } catch (error) {
            console.error(`Failed to delete ${type}:`, error);
            const errorMsg = error.response?.data?.message || error.message || 'Unknown error';
            logDeleteAction(type, {
                id,
                name,
                bulkIds,
                status: 'error',
                errorMessage: error?.message || error?.response?.data?.message || 'Unknown error',
            });
            alert(`Error deleting ${type}: ${errorMsg}`);
        } finally {
            setDeleteModal({ isOpen: false, type: null, id: null, name: '', meta: null });
        }
    };

    return (
        <div className="dev-admin-page">
            <header className="dev-admin-header">
                <h1 className="dev-admin-title">Dev Admin Workspace</h1>
                <div className="dev-admin-actions">
                    <button className="dev-admin-btn danger" onClick={() => logout({ redirectTo: '/auth/login' })}>Logout</button>
                </div>
            </header>

            <div className="dev-admin-content">
                {/* Users List Panel */}
                <div className="dev-admin-panel">
                    <div className="dev-admin-panel-header">
                        <div className="dev-admin-tabs">
                            <button
                                type="button"
                                className={`dev-admin-tab ${activeTab === 'users' ? 'active' : ''}`}
                                onClick={() => setActiveTab('users')}
                            >
                                Users
                            </button>
                            <button
                                type="button"
                                className={`dev-admin-tab ${activeTab === 'clinics' ? 'active' : ''}`}
                                onClick={() => setActiveTab('clinics')}
                            >
                                Clinics
                            </button>
                        </div>
                        <span className="dev-admin-count">
                            {activeTab === 'users'
                                ? `${filteredUsers.length} Users`
                                : `${filteredClinics.length} Clinics`}
                        </span>
                    </div>
                    <div className="dev-admin-search">
                        <input
                            type="text"
                            placeholder={activeTab === 'users' ? "Search users..." : "Search clinics..."}
                            value={activeTab === 'users' ? userSearchQuery : clinicSearchQuery}
                            onChange={(e) => activeTab === 'users' ? setUserSearchQuery(e.target.value) : setClinicSearchQuery(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key !== 'Enter') return;
                                activeTab === 'users' ? loadUsers() : loadClinics();
                            }}
                        />
                    </div>
                    <div className="dev-admin-user-list">
                        {activeTab === 'users' ? (
                            isLoadingUsers && users.length === 0 ? (
                                <div className="dev-admin-skeleton-list">
                                    {Array.from({ length: 8 }).map((_, idx) => (
                                        <div key={idx} className="dev-admin-skeleton-item">
                                            <div className="dev-admin-skeleton-line w-70" />
                                            <div className="dev-admin-skeleton-line w-45" />
                                        </div>
                                    ))}
                                </div>
                            ) : filteredUsers.length === 0 ? (
                                <div className="dev-admin-empty-state">
                                    <p>{userSearchQuery ? 'No users matching search.' : 'No users found.'}</p>
                                    {!userSearchQuery && (
                                        <button
                                            onClick={loadUsers}
                                            className="dev-admin-btn"
                                            style={{ marginTop: '1rem' }}
                                        >
                                            Refresh List
                                        </button>
                                    )}
                                </div>
                            ) : (
                                filteredUsers.map(user => (
                                    <div
                                        key={user.id}
                                        className={`dev-admin-user-item ${selectedUser?.id === user.id ? 'selected' : ''}`}
                                        onClick={() => handleSelectUser(user)}
                                    >
                                        <div className="dev-admin-user-info">
                                            <h4>{getUserName(user)}</h4>
                                            <p>{user.email}</p>
                                        </div>
                                        <span className="dev-admin-user-role">{getUserType(user)}</span>
                                    </div>
                                ))
                            )
                        ) : (
                            isLoadingClinics && clinics.length === 0 ? (
                                <div className="dev-admin-skeleton-list">
                                    {Array.from({ length: 8 }).map((_, idx) => (
                                        <div key={idx} className="dev-admin-skeleton-item">
                                            <div className="dev-admin-skeleton-line w-70" />
                                            <div className="dev-admin-skeleton-line w-45" />
                                        </div>
                                    ))}
                                </div>
                            ) : clinicLoadError ? (
                                <div className="dev-admin-empty-state">
                                    <p>{clinicLoadError}</p>
                                    <button onClick={loadClinics} className="dev-admin-btn" style={{ marginTop: '1rem' }}>
                                        Retry
                                    </button>
                                </div>
                            ) : filteredClinics.length === 0 ? (
                                <div className="dev-admin-empty-state">
                                    <p>{clinicSearchQuery ? 'No clinics matching search.' : 'No clinics found.'}</p>
                                    {!clinicSearchQuery && (
                                        <button
                                            onClick={loadClinics}
                                            className="dev-admin-btn"
                                            style={{ marginTop: '1rem' }}
                                        >
                                            Refresh List
                                        </button>
                                    )}
                                </div>
                            ) : (
                                filteredClinics.map(clinic => (
                                    <div
                                        key={clinic.id}
                                        className={`dev-admin-user-item ${selectedClinic?.id === clinic.id ? 'selected' : ''}`}
                                        onClick={() => handleSelectClinic(clinic)}
                                    >
                                        <div className="dev-admin-user-info">
                                            <h4>{getClinicName(clinic)}</h4>
                                            <p>{clinic.email || clinic.phone || '—'}</p>
                                        </div>
                                        <span className="dev-admin-user-role">CLINIC</span>
                                    </div>
                                ))
                            )
                        )}
                    </div>
                </div>

                {/* Details Panel */}
                <div className="dev-admin-panel">
                    <div className="dev-admin-panel-header">
                        <h2 className="dev-admin-panel-title">Details View</h2>
                        {selectedUser && activeTab === 'users' && (
                            <button
                                className="dev-admin-btn danger"
                                style={{ fontSize: '0.8rem', padding: '0.25rem 0.5rem' }}
                                onClick={() => handleDeleteUserCheck(selectedUser)}
                            >
                                DELETE USER
                            </button>
                        )}
                    </div>
                    <div className="dev-admin-details-content">
                        {activeTab === 'users' ? (
                            isLoadingDetails ? (
                                <div className="dev-admin-skeleton-details">
                                    <div className="dev-admin-skeleton-line w-40" />
                                    <div className="dev-admin-skeleton-grid">
                                        {Array.from({ length: 6 }).map((_, idx) => (
                                            <div key={idx} className="dev-admin-skeleton-card">
                                                <div className="dev-admin-skeleton-line w-35" />
                                                <div className="dev-admin-skeleton-line w-70" />
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            ) : !selectedUser ? (
                                <div className="dev-admin-empty-state">Select a user to view details</div>
                            ) : (
                                <>
                                    <div className="dev-admin-section">
                                        <h3>User Information</h3>
                                        <div className="dev-admin-info-grid">
                                            <div className="dev-admin-info-item">
                                                <label>ID</label>
                                                <span>{safeRender(selectedUser.id)}</span>
                                            </div>
                                            <div className="dev-admin-info-item">
                                                <label>Name</label>
                                                <span>{getUserName(selectedUser)}</span>
                                            </div>
                                            <div className="dev-admin-info-item">
                                                <label>Email</label>
                                                <span>{safeRender(selectedUser.email)}</span>
                                            </div>
                                            <div className="dev-admin-info-item">
                                                <label>Phone</label>
                                                <span>{safeRender(selectedUser.phone) || 'N/A'}</span>
                                            </div>
                                            <div className="dev-admin-info-item">
                                                <label>Registered</label>
                                                <span>{(() => {
                                                    const date = new Date(selectedUser.created_at);
                                                    return isNaN(date.getTime()) ? 'N/A' : date.toLocaleDateString();
                                                })()}</span>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="dev-admin-section">
                                        <h3>Owned Pets ({selectedUser.pets?.length || 0})</h3>
                                        {(!selectedUser.pets || selectedUser.pets.length === 0) ? (
                                            <p className="no-data">No pets registered.</p>
                                        ) : (
                                            <div className="dev-admin-pets-grid">
                                                {selectedUser.pets.map(pet => (
                                                    <div key={pet.id} className="dev-admin-pet-card">
                                                        <img
                                                            src={pet.profile_image || "/images/no-image-pet.png"}
                                                            alt={pet.name || 'Pet'}
                                                            className="dev-admin-pet-img"
                                                            onError={(e) => {
                                                                e.target.onerror = null; // Prevent loop
                                                                e.target.src = "/images/no-image-pet.png";
                                                            }}
                                                        />
                                                        <div className="dev-admin-pet-name">{safeRender(pet.name)}</div>
                                                        <div className="dev-admin-pet-info">{safeRender(pet.species)} • {safeRender(pet.breed)}</div>
                                                        <button
                                                            className="dev-admin-pet-delete"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                handleDeletePetCheck(pet);
                                                            }}
                                                        >
                                                            Delete Pet
                                                        </button>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </>
                            )
                        ) : (
                            isLoadingClinicDetails ? (
                                <div className="dev-admin-skeleton-details">
                                    <div className="dev-admin-skeleton-line w-40" />
                                    <div className="dev-admin-skeleton-grid">
                                        {Array.from({ length: 6 }).map((_, idx) => (
                                            <div key={idx} className="dev-admin-skeleton-card">
                                                <div className="dev-admin-skeleton-line w-35" />
                                                <div className="dev-admin-skeleton-line w-70" />
                                            </div>
                                        ))}
                                    </div>
                                    <div className="dev-admin-skeleton-line w-60" style={{ marginTop: 18 }} />
                                    <div className="dev-admin-skeleton-table">
                                        {Array.from({ length: 6 }).map((_, idx) => (
                                            <div key={idx} className="dev-admin-skeleton-line w-100" style={{ height: 14 }} />
                                        ))}
                                    </div>
                                </div>
                            ) : !selectedClinic ? (
                                <div className="dev-admin-empty-state">Select a clinic to view details</div>
                            ) : clinicDetailsError ? (
                                <div className="dev-admin-empty-state">
                                    <p>{clinicDetailsError}</p>
                                    <button className="dev-admin-btn" onClick={() => handleSelectClinic(selectedClinic)} style={{ marginTop: '1rem' }}>
                                        Retry
                                    </button>
                                </div>
                            ) : (
                                <>
                                    <div className="dev-admin-section">
                                        <h3>Clinic Information</h3>
                                        <div className="dev-admin-info-grid">
                                            <div className="dev-admin-info-item">
                                                <label>ID</label>
                                                <span>{safeRender(selectedClinic.id)}</span>
                                            </div>
                                            <div className="dev-admin-info-item">
                                                <label>Name</label>
                                                <span>{getClinicName(selectedClinic)}</span>
                                            </div>
                                            <div className="dev-admin-info-item">
                                                <label>Email</label>
                                                <span>{safeRender(selectedClinic.email) || 'N/A'}</span>
                                            </div>
                                            <div className="dev-admin-info-item">
                                                <label>Phone</label>
                                                <span>{safeRender(selectedClinic.phone) || safeRender(selectedClinic.mobile) || 'N/A'}</span>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="dev-admin-section">
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
                                            <h3 style={{ margin: 0 }}>Appointments ({clinicAppointments?.length || 0})</h3>
                                            {(Array.isArray(clinicAppointments) && clinicAppointments.length > 0) && (
                                                <button
                                                    type="button"
                                                    className="dev-admin-btn danger"
                                                    style={{ padding: '0.35rem 0.6rem', fontSize: '0.75rem' }}
                                                    onClick={() => handleDeleteClinicBulkCheck('appointments_bulk', clinicAppointments)}
                                                >
                                                    Delete All
                                                </button>
                                            )}
                                        </div>
                                        {(!clinicAppointments || clinicAppointments.length === 0) ? (
                                            <p className="no-data">No appointments found.</p>
                                        ) : (
                                            <div className="dev-admin-table">
                                                <div className="dev-admin-table-row head">
                                                    <div>ID</div><div>Pet</div><div>Owner</div><div>Status</div><div>Date</div><div></div>
                                                </div>
                                                {(Array.isArray(clinicAppointments) ? clinicAppointments : []).slice(0, 50).map((a) => (
                                                    <div key={a.id || `${a.pet_id}-${a.appointment_date}`} className="dev-admin-table-row">
                                                        <div>{safeRender(a.id)}</div>
                                                        <div>{safeRender(a.pet?.name || a.pet_name || a.pet)}</div>
                                                        <div>{safeRender(a.pet_owner?.full_name || a.pet_owner || a.owner)}</div>
                                                        <div>{safeRender(a.status)}</div>
                                                        <div>{safeRender(a.appointment_date || a.date || a.created_at)}</div>
                                                            <div style={{ textAlign: 'right' }}>
                                                                <button
                                                                    type="button"
                                                                    className="dev-admin-btn danger"
                                                                    style={{ padding: '0.25rem 0.5rem', fontSize: '0.75rem' }}
                                                                    onClick={() => handleDeleteClinicItemCheck('appointment', a)}
                                                                >
                                                                    Delete
                                                                </button>
                                                            </div>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>

                                    <div className="dev-admin-section">
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
                                            <h3 style={{ margin: 0 }}>Outpatient Visits ({clinicOutpatientVisits?.length || 0})</h3>
                                            {(Array.isArray(clinicOutpatientVisits) && clinicOutpatientVisits.length > 0) && (
                                                <button
                                                    type="button"
                                                    className="dev-admin-btn danger"
                                                    style={{ padding: '0.35rem 0.6rem', fontSize: '0.75rem' }}
                                                    onClick={() => handleDeleteClinicBulkCheck('outpatient_bulk', clinicOutpatientVisits)}
                                                >
                                                    Delete All
                                                </button>
                                            )}
                                        </div>
                                        {(!clinicOutpatientVisits || clinicOutpatientVisits.length === 0) ? (
                                            <p className="no-data">No outpatient visits found.</p>
                                        ) : (
                                            <div className="dev-admin-table">
                                                <div className="dev-admin-table-row head">
                                                    <div>ID</div><div>Pet</div><div>Owner</div><div>Status</div><div>Date</div><div></div>
                                                </div>
                                                {(Array.isArray(clinicOutpatientVisits) ? clinicOutpatientVisits : []).slice(0, 50).map((v) => (
                                                    <div key={v.id || `${v.pet_id}-${v.visit_date}`} className="dev-admin-table-row">
                                                        <div>{safeRender(v.id)}</div>
                                                        <div>{safeRender(v.pet?.name || v.pet_name || v.pet)}</div>
                                                        <div>{safeRender(v.pet_owner?.full_name || v.pet_owner || v.owner)}</div>
                                                        <div>{safeRender(v.status)}</div>
                                                        <div>{safeRender(v.visit_date || v.date || v.created_at)}</div>
                                                        <div style={{ textAlign: 'right' }}>
                                                            <button
                                                                type="button"
                                                                className="dev-admin-btn danger"
                                                                style={{ padding: '0.25rem 0.5rem', fontSize: '0.75rem' }}
                                                                onClick={() => handleDeleteClinicItemCheck('outpatient_visit', v)}
                                                            >
                                                                Delete
                                                            </button>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>

                                    <div className="dev-admin-section">
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
                                            <h3 style={{ margin: 0 }}>Inpatient Visits ({clinicInpatientVisits?.length || 0})</h3>
                                            {(Array.isArray(clinicInpatientVisits) && clinicInpatientVisits.length > 0) && (
                                                <button
                                                    type="button"
                                                    className="dev-admin-btn danger"
                                                    style={{ padding: '0.35rem 0.6rem', fontSize: '0.75rem' }}
                                                    onClick={() => handleDeleteClinicBulkCheck('inpatient_bulk', clinicInpatientVisits)}
                                                >
                                                    Delete All
                                                </button>
                                            )}
                                        </div>
                                        {(!clinicInpatientVisits || clinicInpatientVisits.length === 0) ? (
                                            <p className="no-data">No inpatient visits found.</p>
                                        ) : (
                                            <div className="dev-admin-table">
                                                <div className="dev-admin-table-row head">
                                                    <div>ID</div><div>Pet</div><div>Owner</div><div>Status</div><div>Date</div><div></div>
                                                </div>
                                                {(Array.isArray(clinicInpatientVisits) ? clinicInpatientVisits : []).slice(0, 50).map((v) => (
                                                    <div key={v.id || `${v.pet_id}-${v.visit_date}`} className="dev-admin-table-row">
                                                        <div>{safeRender(v.id)}</div>
                                                        <div>{safeRender(v.pet?.name || v.pet_name || v.pet)}</div>
                                                        <div>{safeRender(v.pet_owner?.full_name || v.pet_owner || v.owner)}</div>
                                                        <div>{safeRender(v.status)}</div>
                                                        <div>{safeRender(v.visit_date || v.date || v.created_at)}</div>
                                                        <div style={{ textAlign: 'right' }}>
                                                            <button
                                                                type="button"
                                                                className="dev-admin-btn danger"
                                                                style={{ padding: '0.25rem 0.5rem', fontSize: '0.75rem' }}
                                                                onClick={() => handleDeleteClinicItemCheck('inpatient_visit', v)}
                                                            >
                                                                Delete
                                                            </button>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </>
                            )
                        )}
                    </div>
                </div>
            </div>

            <ConfirmModal
                isOpen={deleteModal.isOpen}
            title={
                deleteModal.type === 'user' ? "Delete User" :
                    deleteModal.type === 'pet' ? "Delete Pet" :
                        deleteModal.type === 'appointment' ? "Delete Appointment" :
                            deleteModal.type === 'outpatient_visit' ? "Delete Outpatient Visit" :
                                deleteModal.type === 'inpatient_visit' ? "Delete Inpatient Visit" :
                                    "Confirm Delete"
            }
            message={
                deleteModal.type === 'user'
                    ? `Are you surely you want to delete user "${deleteModal.name}"? This action cannot be undone.`
                    : deleteModal.type === 'pet'
                        ? `Are you sure you want to delete pet "${deleteModal.name}"?`
                        : `Are you sure you want to delete "${deleteModal.name}"? This action cannot be undone.`
            }
                confirmLabel="Delete"
                cancelLabel="Cancel"
                onConfirm={handleConfirmDelete}
                onCancel={() => setDeleteModal({ isOpen: false, type: null, id: null, name: '', meta: null })}
                showWarningIcon={true}
            />
        </div>
    );
};

export default DevAdminPage;
